import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "../../../supabaseClient";
import "../../../assets/css/shipment-trackercss.css";
import terumoLogo from "../../../assets/terumo-logo2.webp";
import linksLogo from "../../../assets/link_16x9.png";

import urcLogo from "../../../assets/urclogo.jpg";
import { motion, AnimatePresence } from "framer-motion";
import {
  getShipmentDisplayStatus,
  getShipmentCurrentProgressStep,
  getShipmentProgressSteps,
  getShipmentStepIconClass,
} from "../../../utils/shipmentProgress";

const findShipmentByReference = async (value) => {
  const normalizedValue = String(value || "").trim().toUpperCase();

  if (!normalizedValue) {
    return { data: null, error: null };
  }

  return supabase
    .from("shipments")
    .select("*")
    .or(
      `tracking_no.eq.${normalizedValue},job_order.eq.${normalizedValue},master_waybill.eq.${normalizedValue},house_waybill.eq.${normalizedValue}`
    )
    .limit(1)
    .maybeSingle();
};

const dedupeDocuments = (documents = []) => {
  const seen = new Set();

  return (Array.isArray(documents) ? documents : []).filter((doc) => {
    const key = `${String(doc?.title || "").trim()}::${String(
      doc?.url || ""
    ).trim()}`;

    if (!String(doc?.url || "").trim() || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const Tracking = () => {
  const { trackingNumber: paramTrackingNumber } = useParams();
  const [trackingNumber, setTrackingNumber] = useState(
    paramTrackingNumber || ""
  );
  const [trackingDetails, setTrackingDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [shipmentETA, setShipmentETA] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [isBrokerageHistoryModalOpen, setIsBrokerageHistoryModalOpen] =
    useState(false);

  const stepDescriptions = getShipmentProgressSteps(trackingDetails);
  const displayStatus = getShipmentDisplayStatus(trackingDetails);
  const currentStep = getShipmentCurrentProgressStep(trackingDetails);

  useEffect(() => {
    // Lock scrolling but keep the scrollbar visible by calculating the scrollbar width
    if (isBrokerageHistoryModalOpen) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      // Disable page scroll while the modal is open
      document.body.style.overflow = "hidden";
      // Add padding to prevent layout shift
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      // Reset body styles when modal is closed
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isBrokerageHistoryModalOpen]);

  // Function to fetch tracking details from Supabase
  const fetchTrackingDetails = async (number) => {
    setLoading(true); // Start loading
    setErrorMessage(""); // Clear any previous errors

    try {
      const normalizedNumber = String(number || "").trim().toUpperCase();
      const { data, error } = await findShipmentByReference(normalizedNumber);

      if (error || !data) {
        console.error("Error fetching tracking details:", error);
        setTrackingDetails(null);
        setDocuments([]);
        setReceivedDate(null);
        // Defer error display until after loading finishes
        setTimeout(() => {
          setErrorMessage(
            "Reference not found. Please check the tracking number, master waybill, or house waybill and try again."
          );
        }, 200);
      } else {
        setTrackingDetails(data);
        setTrackingNumber(data.tracking_no || normalizedNumber);

        if (data.document_urls) {
          const parsedDocs = Array.isArray(data.document_urls)
            ? data.document_urls
            : JSON.parse(data.document_urls);

          // Only show documents that still exist (not deleted) for this shipment
          const { data: liveDocuments, error: liveDocumentsError } =
            await supabase
              .from("documents")
              .select("document_url")
              .eq("shipment_id", data.shipment_id);

          if (liveDocumentsError) {
            console.error(
              "Error fetching live documents:",
              liveDocumentsError
            );
            setDocuments(dedupeDocuments(parsedDocs));
          } else {
            const liveUrls = new Set(
              (liveDocuments || []).map((d) =>
                String(d.document_url || "").trim()
              )
            );
            const availableDocs = parsedDocs.filter((doc) =>
              liveUrls.has(String(doc?.url || "").trim())
            );
            setDocuments(dedupeDocuments(availableDocs));
          }
        } else {
          setDocuments([]);
        }

        if (data.receiveddate) {
          setReceivedDate(data.receiveddate);
        } else {
          setReceivedDate(null);
        }

        setErrorMessage(""); // Clear error
      }
    } catch (err) {
      console.error("Error in fetchTrackingDetails:", err);
      setTrackingDetails(null);
      setDocuments([]);
      setReceivedDate(null);
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false); // Always end loading
    }
  };

  // Function to fetch shipment details including ETA from Supabase
  const fetchShipmentETA = async (shipmentId) => {
    try {
      const { data, error } = await supabase
        .from("shipments")
        .select("eta") // Fetch only the 'eta' column
        .eq("shipment_id", shipmentId) // Ensure you pass the correct shipment ID
        .single(); // Use single to get a single result

      if (error || !data) {
        console.error("Error fetching shipment ETA:", error);
        setShipmentETA(null); // Handle the absence of data
        setErrorMessage(
          "Shipment not found. Please check the ID and try again."
        );
      } else {
        setShipmentETA(data.eta); // Set the fetched ETA
        setErrorMessage(""); // Reset error message if data is found
      }
    } catch (err) {
      console.error("Error in fetchShipmentETA:", err);
      setShipmentETA(null);
      setErrorMessage("An unexpected error occurred. Please try again.");
    }
  };

  useEffect(() => {
    if (paramTrackingNumber) {
      fetchTrackingDetails(paramTrackingNumber);
    }
  }, [paramTrackingNumber]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (trackingNumber) {
      const { data } = await findShipmentByReference(trackingNumber);

      if (data?.tracking_no) {
        setTrackingNumber(data.tracking_no);
        navigate(`/tracking/${data.tracking_no}`);
        return;
      }

      await fetchTrackingDetails(trackingNumber);
    } else {
      setErrorMessage("Please enter a tracking number, master waybill, or house waybill.");
    }
  };

  const formatDate = (dateString) => {
    const options = {
      weekday: "short", // "Thurs"
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      hour12: true, // "4:58 PM"
    };
    const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
      new Date(dateString)
    );
    return formattedDate.replace(",", " at"); // Replace comma with " at"
  };

  function formatDate2(date) {
    if (!date) return ""; // Handle null or undefined date
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Use 12-hour format with AM/PM
    }).format(new Date(date));
  }

  const parseRemarks = () => {
    try {
      return trackingDetails?.remarks || [];
    } catch (error) {
      console.error("Error parsing remarks JSONB:", error);
      return [];
    }
  };

  const getVisibleRemarkLines = (remarkText) =>
    String(remarkText || "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith("Footnote:"));
  const [receivedDate, setReceivedDate] = useState(null);

  useEffect(() => {
    const fetchShipmentDate = async () => {
      const { data, error } = await supabase
        .from("shipments")
        .select("receiveddate")
        .limit(1) // Adjust the query if needed, to filter based on shipment ID or other criteria
        .single(); // Assuming you are fetching one record

      if (error) {
        console.error("Error fetching shipment date:", error);
      } else {
        setReceivedDate(data.receiveddate);
      }
    };

    fetchShipmentDate();
  }, []);
  const renderStatus = () => {
    if (isLoading) {
      return <span>Searching Tracking Number</span>;
    }

    if (
      !trackingDetails ||
      !trackingDetails.remarks ||
      trackingDetails.remarks.length === 0
    ) {
      return <span>No status available</span>;
    }

    const latestRemark = trackingDetails.remarks.reduce((latest, remark) => {
      return new Date(remark.date) > new Date(latest.date) ? remark : latest;
    }, trackingDetails.remarks[0]);

    return (
      <span
        className={`rounded-full px-6 py-3 text-sm font-semibold shadow-lg ${getStatusClass(
          latestRemark.status
        )}`}
      >
        {latestRemark.status}
      </span>
    );
  };
  const getStatusClass = (status) => {
    const statusClasses = {
      "Order Placed": "bg-blue/10 text-blue border border-blue/30",
      "In Warehouse": "bg-yellow/10 text-yellow border border-yellow/30",
      "Departure at Origin": "bg-orange-100 text-orange-700 border border-orange-300",
      "In Transit": "bg-purple-100 text-purple-700 border border-purple-300",
      "Arrival at Destination": "bg-green-100 text-green-700 border border-green-300",
      "Custom Clearance": "bg-teal-100 text-teal-700 border border-teal-300",
      "Delivered": "bg-lime-100 text-lime-700 border border-lime-300",
      "Turnover": "bg-teal-100 text-teal-700 border border-teal-300",
    };

    return statusClasses[status] || "bg-gray-100 text-gray-700 border border-gray-300";
  };
  const latestStatus =
    trackingDetails?.remarks?.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    )?.[0]?.status || "";

  const getBackgroundStyle = () => {
    if (trackingDetails?.corporate_client === "Terumo") {
      return {
        backgroundImage: `url(${terumoLogo})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top center",
        backgroundSize: "120px",
      };
    }
    if (trackingDetails?.corporate_client === "URC") {
      return {
        backgroundImage: `url(${urcLogo})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top center",
        backgroundSize: "120px",
      };
    }
    if (trackingDetails?.corporate_client === "Links") {
      return {
        backgroundImage: `url(${linksLogo})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top center",
        backgroundSize: "120px",
      };
    }
    return {};
  };

  return (
    <section
      id="tracking_status"
      className="relative min-h-screen flex flex-col font-poppins bg-gradient-to-br from-gray-50 to-blue/5 overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl"></div>
      </div>

      {/* Corporate Client Watermark */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-3">
        {trackingDetails?.corporate_client === "URC" && (
          <img
            src={urcLogo}
            alt="URC Watermark"
            className="w-full h-full object-cover"
          />
        )}
        {trackingDetails?.corporate_client === "Terumo" && (
          <img
            src={terumoLogo}
            alt="Terumo Watermark"
            className="w-full h-full object-cover"
          />
        )}
        {trackingDetails?.corporate_client === "Links" && (
          <img
            src={linksLogo}
            alt="Links Watermark"
            className="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="relative z-10 flex flex-col items-center px-4 py-10 min-h-[90vh] max-w-screen-2xl mx-auto w-full">
        {/* Header Section */}
        <div className="w-full max-w-6xl mx-auto mb-6 sm:mb-8">
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 p-4 sm:p-6 md:p-8">
            <div className="flex flex-col gap-4 sm:gap-6 md:flex-row md:justify-between md:items-center">
              {/* Title */}
              <div className="text-center md:text-left">
                <h1 className="font-poppins text-blue text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
                  Order Tracking
                </h1>
                <p className="text-grey text-base sm:text-lg font-medium">
                  Track shipments using tracking number
                </p>
              </div>

              {/* Search Bar */}
              <div className="w-full md:max-w-md">
                <form
                  onSubmit={handleSearch}
                  className="flex w-full"
                >
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                      <i className="fas fa-barcode text-blue/50 text-base sm:text-lg"></i>
                    </div>
                    <input
                      type="text"
                      placeholder="Enter tracking number or waybill..."
                      className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 border-2 border-gray-200 rounded-l-xl sm:rounded-l-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md text-sm sm:text-base font-medium placeholder-grey/60"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    className="bg-blue hover:from-blue/90 hover:to-blue text-white px-4 sm:px-6 py-3 sm:py-4 rounded-r-xl sm:rounded-r-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center gap-2 font-semibold text-sm sm:text-base"
                  >
                    <i className="fas fa-search"></i>
                    <span className="hidden xs:block">Search</span>
                  </button>
                </form>
                <p className="text-xs text-grey/70 mt-2 text-center md:text-left">
                  Enter your tracking number, master waybill, or house waybill to view shipment details
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <div className="w-full max-w-6xl mx-auto mb-8 sm:mb-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={trackingDetails?.remarks?.[0]?.status || "loading"}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white/90 backdrop-blur-xl border border-blue/10 shadow-2xl rounded-2xl sm:rounded-3xl p-6 sm:p-8"
            >
              {loading ? (
                <div className="h-[150px] sm:h-[200px] flex flex-col justify-center items-center gap-4">
                  <motion.div
                    className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue/20 border-t-blue rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-blue font-semibold text-base sm:text-lg text-center"
                  >
                    Searching for your package...
                  </motion.div>
                </div>
              ) : (
                <motion.div
                  className="text-center space-y-4 sm:space-y-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  {/* Status Display */}
                  <div className="flex flex-col items-center gap-3 sm:gap-4">
                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
                        <i className="fas fa-shipping-fast text-blue text-lg sm:text-xl"></i>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-blue">Current Status</h2>
                    </div>
                    
                    <motion.div
                      className="flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {renderStatus()}
                    </motion.div>
                  </div>

                  {/* Date section */}
                  {!loading && trackingDetails?.remarks?.length > 0 && (
                    <motion.div
                      className="inline-flex flex-col sm:flex-row items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-200"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5, duration: 0.3 }}
                    >
                      <i className="fas fa-calendar-alt text-blue text-sm sm:text-base"></i>
                      <span className="text-grey font-medium text-sm sm:text-base text-center">
                        Last updated: {`${new Intl.DateTimeFormat("en-US", {
                          month: "long",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone: "Asia/Manila",
                        }).format(
                          new Date(
                            trackingDetails.remarks.sort(
                              (a, b) => new Date(b.date) - new Date(a.date)
                            )[0]?.date
                          )
                        )}`}
                      </span>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {!loading && trackingDetails && !errorMessage && (
          <>
            {/* Progress Stepper */}
            <div className="w-full max-w-6xl mx-auto mb-8 sm:mb-12">
              <div className="bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 p-6 sm:p-8">
                <div className="mb-6 sm:mb-8 text-center">
                  <h2 className="text-xl sm:text-2xl font-bold text-blue mb-2">Shipment Progress</h2>
                  <p className="text-grey font-medium text-sm sm:text-base">Track your package journey step by step</p>
                </div>

                <div className="relative flex flex-col sm:flex-row justify-center w-full">
                  {/* Desktop Progress Line */}
                  <div className="absolute top-8 left-0 right-0 mx-auto w-[85%] h-1 bg-gray-200 z-0 hidden sm:block rounded-full"></div>

                  {/* Mobile Progress Line - Centered vertically through icons */}
                  <div className="absolute top-8 bottom-8 left-8 w-1 bg-gray-200 z-0 sm:hidden rounded-full"></div>

                  {/* Desktop Blue Progress Bar */}
                  <div className="absolute top-8 left-0 right-0 mx-auto w-[85%] hidden sm:block">
                    <div
                      className="h-1 bg-gradient-to-r from-blue to-blue/80 z-0 transition-all duration-1000 ease-out rounded-full"
                      style={{
                        width: `${
                          currentStep && stepDescriptions.length
                            ? Math.min(
                                ((currentStep - 1) /
                                  (stepDescriptions.length - 1)) *
                                  100,
                                100
                              )
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>

                  {/* Mobile Blue Progress Bar */}
                  <div
                    className="absolute top-8 left-8 w-1 bg-gradient-to-b from-blue to-blue/80 z-0 transition-all duration-1000 ease-out sm:hidden rounded-full"
                    style={{
                      height: `${
                        currentStep && stepDescriptions.length > 1
                          ? Math.min(
                              ((currentStep - 1) / 
                                (stepDescriptions.length - 1)) * 100,
                              100
                            )
                          : 0
                      }%`,
                    }}
                  ></div>

                  {/* Steps */}
                  <div className="flex flex-col sm:flex-row w-full space-y-8 sm:space-y-0 sm:gap-0">
                    {stepDescriptions.map((step, index) => {
                      const remarkDetail = trackingDetails.remarks?.find(
                        (remark) => remark.status === step
                      );

                      const formattedDate = remarkDetail?.date
                        ? new Intl.DateTimeFormat("en-US", {
                            weekday: "short",
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })
                            .format(new Date(remarkDetail.date))
                            .replace(",", " at")
                        : "Pending";

                      const isActive = index + 1 <= currentStep;
                      const isCurrent = index + 1 === currentStep;

                      return (
                        <div
                          key={index}
                          className="relative z-10 flex flex-row sm:flex-col items-center sm:flex-1"
                        >
                          {/* Icon Container */}
                          <div className="flex flex-col justify-center items-center flex-shrink-0">
                            <div
                              className={`w-16 h-16 rounded-2xl border-2 flex items-center justify-center shadow-lg transition-all duration-300 ${
                                isActive
                                  ? "bg-blue text-white border-blue shadow-blue/30"
                                  : "bg-white text-grey border-gray-200 shadow-gray/20"
                              } ${isCurrent ? 'shadow-blue/50' : ''}`}
                              style={isCurrent ? {
                                animation: 'bounce 3s ease-in-out infinite alternate'
                              } : {}}
                            >
                              <i
                                className={`${getShipmentStepIconClass(
                                  step,
                                  trackingDetails
                                )} text-[20px]`}
                              />
                            </div>
                            
                            {isCurrent && (
                              <div className="w-2 h-2 bg-yellow rounded-full mt-2 animate-ping opacity-75" style={{
                                animationDuration: '3s'
                              }} />
                            )}
                          </div>

                          {/* Text Content */}
                          <div className="flex flex-col sm:items-center ml-6 sm:ml-0 mt-0 sm:mt-4 flex-1 min-w-0">
                            <h3 className={`font-semibold text-sm sm:text-center transition-colors duration-300 ${
                              isCurrent ? "text-blue" : isActive ? "text-blue/80" : "text-grey"
                            }`}>
                              {step}
                            </h3>
                            {/* <p className={`text-xs mt-1 sm:text-center transition-colors duration-300 ${
                              isActive ? "text-grey" : "text-grey/60"
                            }`}>
                              {formattedDate}
                            </p> */}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="w-full max-w-6xl mx-auto mb-8 sm:mb-12">
              <div className="bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
                    <i className="fas fa-file-alt text-blue text-lg sm:text-xl"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-blue">Documents</h2>
                </div>
                
                {documents.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {documents.map((doc, index) => (
                      <div
                        key={index}
                        className="group bg-gradient-to-r from-blue/5 to-blue/10 hover:from-blue/10 hover:to-blue/20 p-4 rounded-xl sm:rounded-2xl border border-blue/20 hover:border-blue/40 transition-all duration-300"
                      >
                        <a
                          href={doc.url}
                          download
                          className="flex items-center gap-3 text-blue hover:text-blue/80 transition-colors duration-300"
                        >
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue/10 group-hover:bg-blue/20 rounded-lg sm:rounded-xl flex items-center justify-center transition-colors duration-300 flex-shrink-0">
                            <i className="fas fa-download text-blue text-sm sm:text-base"></i>
                          </div>
                          <span className="font-semibold group-hover:underline text-sm sm:text-base">{doc.title}</span>
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <i className="fas fa-file-alt text-gray-400 text-xl sm:text-2xl"></i>
                    </div>
                    <p className="text-grey font-medium text-sm sm:text-base">No documents available for download.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {(trackingDetails?.shipment_type === "BROKERAGE" || trackingDetails) && !loading && (
              <div className="w-full max-w-6xl mx-auto mb-8 sm:mb-12">
                <div className="bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 p-4 sm:p-6">
                  <div className="flex justify-center">
                    <button
                      onClick={() => setShowModal(true)}
                      className="bg-blue hover:from-blue/90 hover:to-blue text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center gap-2 sm:gap-3 text-sm sm:text-base"
                    >
                      <i className="fas fa-eye"></i>
                      <span className="whitespace-nowrap">View Shipment Details</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Status History Table */}
            <div className="w-full max-w-6xl mx-auto">
              <div className="bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 sm:mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
                      <i className="fas fa-history text-blue text-lg sm:text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-blue">Shipment History</h2>
                      <p className="text-grey font-medium text-sm sm:text-base">Complete timeline of your package journey</p>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl sm:rounded-2xl border border-gray-200 shadow-lg">
                  {/* Desktop Table */}
                  <div className="hidden md:block">
                    <table className="w-full">
                      <thead className="bg-blue text-white">
                        <tr>
                          <th className="px-4 sm:px-6 py-3 sm:py-4 text-left font-semibold text-sm sm:text-base">Status</th>
                          <th className="px-4 sm:px-6 py-3 sm:py-4 text-left font-semibold text-sm sm:text-base">Details & Remarks</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {parseRemarks().length > 0 ? (
                          parseRemarks()
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((remark, index) => (
                              <tr
                                key={index}
                                className="hover:bg-gray-50 transition-colors duration-200"
                              >
                                <td className="px-4 sm:px-6 py-4 sm:py-6">
                                  <span
                                    className={`inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl sm:rounded-2xl ${getStatusClass(
                                      remark.status
                                    )}`}
                                  >
                                    {remark.status || "No Status"}
                                  </span>
                                </td>
                                <td className="px-4 sm:px-6 py-4 sm:py-6 text-grey leading-relaxed text-sm sm:text-base">
                                  {/* Handle multi-line remarks */}
                                  <div className="space-y-2">
                                    {remark.status === "In Transit" ? (
                                      <>
                                        {remark.remark
                                          ? getVisibleRemarkLines(remark.remark).map((line, idx) => (
                                                <div key={idx}>{line}</div>
                                              ))
                                          : "No remarks available"}
                                      </>
                                    ) : remark.remark ? (
                                      getVisibleRemarkLines(remark.remark).map((line, idx) => (
                                        <div key={idx}>{line}</div>
                                      ))
                                    ) : (
                                      "No remarks available"
                                    )}

                                    {/* Order Placed Extra Info */}
                                    {remark.status === "Order Placed" && (
                                      <div className="text-xs sm:text-sm text-blue font-semibold mt-2">
                                        <strong>Shipment Nomination Date: </strong>
                                        {remark.receivedDate
                                          ? new Date(
                                              remark.receivedDate
                                            ).toLocaleDateString("en-US", {
                                              month: "long",
                                              day: "numeric",
                                              year: "numeric",
                                            })
                                          : "Not set"}
                                      </div>
                                    )}

                                    {/* ETD */}
                                    {(remark.status === "Order Placed" ||
                                      remark.status === "Departure at Origin") && (
                                      <div className="text-xs sm:text-sm text-grey mt-1">
                                        <strong>ETD: </strong>
                                        {trackingDetails.etd
                                          ? new Date(
                                              trackingDetails.etd
                                            ).toLocaleDateString("en-US", {
                                              month: "long",
                                              day: "numeric",
                                              year: "numeric",
                                            })
                                          : "Not available"}
                                      </div>
                                    )}

                                    {/* ATD */}
                                    {remark.status === "Departure at Origin" && (
                                      <div className="text-xs sm:text-sm text-grey mt-1">
                                        <strong>ATD: </strong>
                                        {remark.atd
                                          ? new Date(remark.atd).toLocaleDateString(
                                              "en-US",
                                              {
                                                month: "long",
                                                day: "numeric",
                                                year: "numeric",
                                              }
                                            )
                                          : "Not available"}
                                      </div>
                                    )}

                                    {/* ETA */}
                                    {(remark.status === "Order Placed" ||
                                      remark.status ===
                                        "Arrival at Destination") && (
                                      <div className="text-xs sm:text-sm text-grey mt-1">
                                        <strong>ETA: </strong>
                                        {trackingDetails.eta
                                          ? new Date(
                                              trackingDetails.eta
                                            ).toLocaleDateString("en-US", {
                                              month: "long",
                                              day: "numeric",
                                              year: "numeric",
                                            })
                                          : "Not available"}
                                      </div>
                                    )}

                                    {/* ATA */}
                                    {remark.status === "Arrival at Destination" && (
                                      <div className="text-xs sm:text-sm text-grey mt-1">
                                        <strong>ATA: </strong>
                                        {remark.aad
                                          ? new Date(remark.aad).toLocaleDateString(
                                              "en-US",
                                              {
                                                month: "long",
                                                day: "numeric",
                                                year: "numeric",
                                              }
                                            )
                                          : "Not available"}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td colSpan="2" className="px-4 sm:px-6 py-8 sm:py-12 text-center">
                              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                <i className="fas fa-history text-gray-400 text-lg sm:text-2xl"></i>
                              </div>
                              <p className="text-grey font-medium text-sm sm:text-base">No shipment history available.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-3 sm:space-y-4 p-2">
                    {parseRemarks().length > 0 ? (
                      parseRemarks()
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((remark, index) => (
                          <div
                            key={index}
                            className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm"
                          >
                            <div className="space-y-3">
                              <span
                                className={`inline-flex items-center px-3 py-2 text-xs font-semibold rounded-xl ${getStatusClass(
                                  remark.status
                                )}`}
                              >
                                {remark.status || "No Status"}
                              </span>
                              
                              <div className="text-grey leading-relaxed text-sm">
                                {remark.status === "In Transit" ? (
                                  <>
                                    {remark.remark
                                      ? getVisibleRemarkLines(remark.remark).map((line, idx) => (
                                            <div key={idx}>{line}</div>
                                          ))
                                      : "No remarks available"}
                                  </>
                                ) : remark.remark ? (
                                  getVisibleRemarkLines(remark.remark).map((line, idx) => (
                                    <div key={idx}>{line}</div>
                                  ))
                                ) : (
                                  "No remarks available"
                                )}

                                {/* Additional details for mobile */}
                                {remark.status === "Order Placed" && (
                                  <div className="text-blue font-semibold mt-2 text-xs">
                                    <strong>Shipment Nomination Date: </strong>
                                    {remark.receivedDate
                                      ? new Date(
                                          remark.receivedDate
                                        ).toLocaleDateString("en-US", {
                                          month: "long",
                                          day: "numeric",
                                          year: "numeric",
                                        })
                                      : "Not set"}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="text-center py-8 sm:py-12">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                          <i className="fas fa-history text-gray-400 text-lg sm:text-2xl"></i>
                        </div>
                        <p className="text-grey font-medium text-sm sm:text-base">No shipment history available.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="w-full max-w-6xl mx-auto">
            <div className="bg-red/10 border border-red/30 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-red/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-circle text-red text-xl"></i>
              </div>
              <p className="text-red font-semibold">{errorMessage}</p>
            </div>
          </div>
        )}
      </div>

      {/* Brokerage History Modal */}
      {isBrokerageHistoryModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white/95 backdrop-blur-xl w-full max-w-2xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-blue px-4 sm:px-8 py-4 sm:py-6 relative">
              <button
                onClick={() => setIsBrokerageHistoryModalOpen(false)}
                className="absolute top-2 right-2 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl flex items-center justify-center text-white transition-all duration-200"
              >
                <i className="fas fa-times text-sm sm:text-lg"></i>
              </button>
              <div className="flex items-center gap-3 sm:gap-4 pr-10 sm:pr-0">
                <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl">
                  <i className="fas fa-history text-white text-lg sm:text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg sm:text-2xl mb-1">Brokerage History</h2>
                  <p className="text-white/80 text-xs sm:text-sm">Complete brokerage timeline</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-8">
              {trackingDetails?.brokerage_remarks?.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {trackingDetails.brokerage_remarks.map((entry, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100"
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue/10 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-clipboard-list text-blue text-sm sm:text-base"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col gap-2">
                            <span className="text-xs text-grey font-medium">
                              {new Date(entry.timestamp).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  timeZone: "Asia/Manila",
                                }
                              )}
                              ,{" "}
                              {new Date(entry.timestamp).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "Asia/Manila",
                                }
                              )}
                            </span>
                            <h3 className="font-bold text-blue text-base sm:text-lg break-words">
                              {entry.brokerage_title}
                            </h3>
                            <p className="text-grey leading-relaxed text-sm sm:text-base break-words">
                              {entry.brokerage_input || "No additional remarks"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <i className="fas fa-history text-gray-400 text-lg sm:text-2xl"></i>
                  </div>
                  <p className="text-grey font-medium text-sm sm:text-base">No brokerage history available yet.</p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-200">
              <div className="flex justify-center">
                <button
                  onClick={() => setIsBrokerageHistoryModalOpen(false)}
                  className="px-6 sm:px-8 py-2 sm:py-3 bg-blue hover:from-blue/90 hover:to-blue text-white font-semibold rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipment Details Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white/95 backdrop-blur-xl w-full max-w-4xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-blue px-4 sm:px-8 py-4 sm:py-6 relative">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-2 right-2 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl flex items-center justify-center text-white transition-all duration-200"
              >
                <i className="fas fa-times text-sm sm:text-lg"></i>
              </button>
              <div className="flex items-center gap-3 sm:gap-4 pr-10 sm:pr-0">
                <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl">
                  <i className="fas fa-shipping-fast text-white text-lg sm:text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg sm:text-2xl mb-1">Shipment Details</h2>
                  <p className="text-white/80 text-xs sm:text-sm">Complete shipment information</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-8">
              <div className="space-y-6 sm:space-y-8">
                {/* Shipper Details */}
                <section className="bg-gradient-to-r from-blue/5 to-blue/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue/20">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue/20 rounded-lg sm:rounded-2xl flex items-center justify-center">
                      <i className="fas fa-user-tie text-blue text-sm sm:text-base"></i>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-blue">Shipper Details</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {[
                      { label: "Name", value: trackingDetails.shipper_name, icon: "fas fa-user" },
                      { label: "Unit", value: trackingDetails.shipper_unit, icon: "fas fa-building" },
                      { label: "Email", value: trackingDetails.shipper_email, icon: "fas fa-envelope" },
                      { label: "City", value: trackingDetails.shipper_city, icon: "fas fa-city" },
                      { label: "Street", value: trackingDetails.shipper_street, icon: "fas fa-road" },
                      { label: "Country", value: trackingDetails.shipper_country, icon: "fas fa-flag" },
                      { label: "Phone", value: trackingDetails.shipper_phone, icon: "fas fa-phone" },
                      { label: "Building", value: trackingDetails.shipper_building, icon: "fas fa-building" },
                    ].map((item, index) => (
                      <div key={index} className="bg-white/50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <i className={`${item.icon} text-blue text-xs sm:text-sm`}></i>
                          <span className="text-xs sm:text-sm font-semibold text-grey">{item.label}</span>
                        </div>
                        <span className="text-sm sm:text-base font-bold text-blue break-words">
                          {item.value || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Receiver Details */}
                <section className="bg-gradient-to-r from-blue/5 to-blue/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue/20">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue/20 rounded-lg sm:rounded-2xl flex items-center justify-center">
                      <i className="fas fa-user-check text-blue text-sm sm:text-base"></i>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-blue">Receiver Details</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {[
                      { label: "Name", value: trackingDetails.receiver_name, icon: "fas fa-user" },
                      { label: "Unit", value: trackingDetails.receiver_unit, icon: "fas fa-building" },
                      { label: "Email", value: trackingDetails.receiver_email, icon: "fas fa-envelope" },
                      { label: "City", value: trackingDetails.receiver_city, icon: "fas fa-city" },
                      { label: "Street", value: trackingDetails.receiver_street, icon: "fas fa-road" },
                      { label: "Country", value: trackingDetails.receiver_country, icon: "fas fa-flag" },
                      { label: "Phone", value: trackingDetails.receiver_phone, icon: "fas fa-phone" },
                      { label: "Building", value: trackingDetails.receiver_building, icon: "fas fa-building" },
                    ].map((item, index) => (
                      <div key={index} className="bg-white/50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <i className={`${item.icon} text-blue text-xs sm:text-sm`}></i>
                          <span className="text-xs sm:text-sm font-semibold text-grey">{item.label}</span>
                        </div>
                        <span className="text-sm sm:text-base font-bold text-blue break-words">
                          {item.value || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Shipment Details */}
                <section className="bg-gradient-to-r from-blue/5 to-blue/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue/20">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue/20 rounded-lg sm:rounded-2xl flex items-center justify-center">
                      <i className="fas fa-shipping-fast text-blue text-sm sm:text-base"></i>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-blue">Shipment Information</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {[
                      { label: "Shipment ID", value: trackingDetails.shipment_id, icon: "fas fa-hashtag" },
                      { label: "Job Order", value: trackingDetails.job_order, icon: "fas fa-clipboard-list" },
                      { label: "Master Waybill", value: trackingDetails.master_waybill, icon: "fas fa-barcode" },
                      { label: "House Waybill", value: trackingDetails.house_waybill, icon: "fas fa-barcode" },
                      { label: "Type", value: trackingDetails.shipment_type, icon: "fas fa-tag" },
                      { label: "Origin", value: trackingDetails.shipper_city, icon: "fas fa-map-marker-alt" },
                      { label: "Destination", value: trackingDetails.receiver_city, icon: "fas fa-map-marker-alt" },
                      { label: "Status", value: displayStatus, icon: "fas fa-info-circle" },
                      { label: "Created By", value: trackingDetails.created_by, icon: "fas fa-user-plus" },
                      { label: "Description", value: trackingDetails.shipment_description, icon: "fas fa-file-text" },
                      { label: "Shipment Code", value: trackingDetails.shipment_code, icon: "fas fa-barcode" },
                    ].map((item, index) => (
                      <div key={index} className="bg-white/50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <i className={`${item.icon} text-blue text-xs sm:text-sm`}></i>
                          <span className="text-xs sm:text-sm font-semibold text-grey">{item.label}</span>
                        </div>
                        <span className="text-sm sm:text-base font-bold text-blue break-words">
                          {item.value || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>

            <div className="bg-gray-50 px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-200">
              <div className="flex justify-center">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 sm:px-8 py-2 sm:py-3 bg-blue hover:from-blue/90 hover:to-blue text-white font-semibold rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center gap-2 text-sm sm:text-base"
                >
                  <i className="fas fa-times"></i>
                  Close Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Tracking;
